import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

export namespace DateTime {
	/**
	 * Get the current date and time
	 */
	export function getDateTime(): Date {
		dayjs.extend(utc);
		const localTime = dayjs().utc().local();
		return localTime.toDate();
	}

	export namespace DateTimeUnitTransformer {
		/**
		 * This function adapts a date format string to the format used by Day.js.
		 * It takes a format string as input, which can contain date and time dev-cycle-analyzer.
		 * The format string is split into its dev-cycle-analyzer, and each component is transformed
		 * based on whether it's a date or time component.
		 *
		 * @param {string} formatString - The format string to be adapted.
		 * @returns {string} - The adapted format string.
		 */
		export function adaptDateFormatToDayjs(formatString: string): string {
			// Define mappings for date and time format parts to their Day.js equivalents
			const dateFormatMapping: {[key: string]: string} = {
				dd: 'DD',
				DD: 'DD',
				mm: 'MM', // Month
				MM: 'MM', // Month
				yyyy: 'YYYY',
				YYYY: 'YYYY',
			};

			const timeFormatMapping: {[key: string]: string} = {
				hh: 'HH',
				HH: 'HH',
				mm: 'mm', // Minute
				MM: 'mm', // Minute, considering uppercase input for minutes
				ss: 'ss',
				SS: 'ss',
			};

			// Split the formatString by space to separate date and time dev-cycle-analyzer
			const components = formatString?.split(' ');

			if (!components) return '';

			// Transform each component based on whether it's a date or time component
			const transformedComponents = components.map(component => {
				let adaptedComponent = component;

				// Determine if the component is more likely a date or time part
				// by checking for the presence of date-specific or time-specific parts
				const isTimeComponent = /hh|HH|ss|SS/.test(component);

				// Use the appropriate mapping object based on the component type
				const mapping = isTimeComponent ? timeFormatMapping : dateFormatMapping;

				// Replace all format parts using the selected mapping
				Object.keys(mapping).forEach(key => {
					const regex = new RegExp(key, 'g');
					adaptedComponent = adaptedComponent.replace(regex, mapping[key]);
				});

				return adaptedComponent;
			});

			// Rejoin the transformed dev-cycle-analyzer to form the final adapted format
			return transformedComponents.join(' ');
		}
	}
}
